import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiInfoBanner, NoddiLink } from 'noddi-ui-web';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';

const ADMIN_NAME_LIMIT = 5;

type Props = {
  userGroupId: number;
  organizationName: string;
  /** Leasing company or car dealership: the member is staff, and the cars on screen belong to the company. */
  holdsExternalCars: boolean;
};

/**
 * Tells a member without the admin role why Home shows only part of the fleet, and who to ask for
 * more. Plain members may list the group's members (names only), so the admins can be named.
 * An employee of a leasing company or dealership is acting for the company, so that explanation
 * does not apply.
 */
export const OrganizationMemberAccessInfo = ({ userGroupId, organizationName, holdsExternalCars }: Props) => {
  const { brandEmail } = useCurrentBrand();
  const { data: admins, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupMembers,
    input: { userGroupId, isAdmin: true, pageSize: ADMIN_NAME_LIMIT },
    queryConfig: { enabled: !holdsExternalCars }
  });

  if (holdsExternalCars) {
    return (
      <NoddiInfoBanner
        title={t`You are acting for ${organizationName}`}
        texts={[
          {
            value: t`You see the company's own cars. Cars leased to a customer stay on that customer's account.`
          }
        ]}
      />
    );
  }

  const adminNames = (admins?.results ?? []).map((admin) => `${admin.user.firstName} ${admin.user.lastName}`.trim());
  const names = adminNames.join(', ');
  const hiddenAdminCount = (admins?.count ?? 0) - adminNames.length;
  const adminLine =
    admins === undefined
      ? undefined
      : adminNames.length === 0
        ? t`Your organization has no administrator yet. Contact us and we will help you set one up.`
        : hiddenAdminCount > 0
          ? t`Your administrators: ${names} and ${hiddenAdminCount} more.`
          : t`Your administrators: ${names}.`;

  return (
    <div className='flex flex-col gap-2'>
      <NoddiInfoBanner
        title={t`You see ${organizationName} as a driver`}
        texts={[
          {
            value: t`You see the cars you drive and the bookings for them. Only administrators see the whole fleet and choose who drives each car.`
          },
          ...(adminLine ? [{ value: adminLine }] : []),
          { value: t`Don't know who your administrator is, or need access to more cars? Contact us.` }
        ]}
        actionNode={
          <NoddiLink size='small' variant='link' className='p-0' startIcon='Mail' to={`mailto:${brandEmail}`}>
            {brandEmail}
          </NoddiLink>
        }
      />
      {error && <ApiErrorMessageWithTrans error={error} />}
    </div>
  );
};
